import { Box, Circle, Flex, VStack } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { ProductState } from "flows/Picking/stores/pickingTypes";
import {
  ScanSlot,
  ScanSlotStatus,
  useScanProgressStore,
} from "flows/Picking/stores/useScanProgressStore";
import { IntlMessageId } from "shared/lang";
import { CheckFilledCircleIcon, NewScanningIcon, WarningFilledCircleIcon } from "ui/Icons/Icons";
import { TitleS, TitleXS } from "ui/Typography/Typography";

type ScanProgressListProps = {
  itemId: string;
  totalQuantity: number;
  pickedQuantity: number;
  state: ProductState;
  isSkippedItem?: boolean;
};

const slotConfig: Record<
  ScanSlotStatus,
  {
    titleId: IntlMessageId;
    bg: string;
    borderStyle?: string;
    borderColor?: string;
    textColor: string;
  }
> = {
  scanned: {
    titleId: "components.picking.scan-progress.slot-scanned",
    bg: "blueFlink.100",
    textColor: "blue.500",
  },
  reported: {
    titleId: "components.picking.scan-progress.slot-reported",
    bg: "red.100",
    textColor: "red.500",
  },
  skipped: {
    titleId: "components.picking.scan-progress.slot-skipped",
    bg: "orange.100",
    textColor: "orange.500",
  },
  refund: {
    titleId: "components.picking.scan-progress.slot-refund",
    bg: "orange.100",
    textColor: "orange.500",
  },
  scan_now: {
    titleId: "components.picking.scan-progress.slot-scan-now",
    bg: "grey.150",
    borderStyle: "2px dashed",
    borderColor: "blue.500",
    textColor: "grey.800",
  },
  pending: {
    titleId: "components.picking.scan-progress.slot-pending",
    bg: "grey.100",
    textColor: "grey.450",
  },
};

function SlotIcon({ status }: { status: ScanSlotStatus }) {
  switch (status) {
    case "scanned":
    case "reported":
      return <CheckFilledCircleIcon boxSize="32px" color="veggie.500" />;
    case "skipped":
    case "refund":
      return <WarningFilledCircleIcon boxSize="32px" color="orange.500" />;
    case "scan_now":
      return (
        <Flex h="32px" w="32px" alignItems="center">
          <NewScanningIcon boxSize="26px" color="blue.500" pb="2px" />
        </Flex>
      );

    case "pending":
      return null;
  }
}

function ScanSlotItem({ slot }: { slot: ScanSlot }) {
  const intl = useIntl();
  const config = slotConfig[slot.status];

  return (
    <Flex
      alignItems="center"
      gap="12px"
      px="12px"
      py="8px"
      h="48px"
      w="100%"
      borderRadius="6px"
      bg={config.bg}
      border={config.borderStyle}
      borderColor={config.borderColor}
    >
      {slot.status === "pending" ? (
        <Circle size="32px" bg="white" border="2px solid" borderColor="grey.200">
          <TitleXS color="grey.200" fontWeight={700}>
            {slot.index + 1}
          </TitleXS>
        </Circle>
      ) : (
        <SlotIcon status={slot.status} />
      )}
      <TitleS color={config.textColor} fontSize="0.75rem">
        {intl.formatMessage({ id: config.titleId }, { number: slot.index + 1 })}
      </TitleS>
    </Flex>
  );
}

export function ScanProgressList({
  itemId,
  totalQuantity,
  pickedQuantity,
  state,
  isSkippedItem = false,
}: ScanProgressListProps) {
  const intl = useIntl();
  const slots = useScanProgressStore((s) => s.scanSlots[itemId]);

  if (!slots) return null;

  const isAllScanned = state === "DONE" && pickedQuantity === totalQuantity;
  const hasRefundedSlots = slots.some((s) => s.status === "refund");

  const progressPercent = totalQuantity > 0 ? (pickedQuantity / totalQuantity) * 100 : 0;
  const trackBg = hasRefundedSlots ? "orange.500" : isSkippedItem ? "orange.200" : "grey.200";

  return (
    <Box mt="s200">
      {!isAllScanned && (
        <Box h="8px" w="100%" bg={trackBg} borderRadius="full" mb="s100" overflow="hidden">
          <Box
            h="100%"
            w={`${progressPercent}%`}
            bg="blueFlink.600"
            borderRadius="full"
            transition="width 0.2s ease"
          />
        </Box>
      )}
      <Flex alignItems="center" justifyContent="space-between" mb="s100">
        <TitleS color="grey.800">
          {intl.formatMessage({
            id: isAllScanned
              ? "components.picking.scan-progress.all-scanned"
              : "components.picking.scan-progress.header",
          })}
        </TitleS>
        <Flex
          h="20px"
          px="8px"
          py="2px"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          borderRadius="12px"
          bg={isAllScanned ? "veggie.500" : "blueFlink.500"}
        >
          <TitleXS color="white">
            {pickedQuantity}/{totalQuantity}
          </TitleXS>
        </Flex>
      </Flex>
      <VStack spacing="s50" align="stretch">
        {slots.map((slot) => (
          <ScanSlotItem key={slot.index} slot={slot} />
        ))}
      </VStack>
    </Box>
  );
}
